<template>
  <div class="sg">
    <!-- Ricerca ------------------------------------------------------- -->
    <header class="sg__header">
      <div class="sg__intro">
        <h1 class="sg__title">Log invii SendGrid</h1>
        <p class="sg__lede">
          Storico di consegna e lettura delle mail inviate dai sistemi ASK.
          Cerca un destinatario per vedere cosa è successo a ogni messaggio.
        </p>
      </div>

      <form class="sg__form" @submit.prevent="cerca()">
        <div class="sg__field">
          <label class="sg__label" for="sg-email">Indirizzo destinatario</label>
          <AutoComplete
            id="sg-email"
            v-model="email"
            :suggestions="suggerimenti"
            :delay="250"
            :minLength="3"
            field="email"
            inputClass="sg__input"
            class="sg__autocomplete"
            placeholder="nome@dominio.it"
            @complete="cercaSuggerimenti"
            @item-select="onSuggerimentoScelto"
            @keyup.enter="cerca()"
          >
            <template #item="slot">
              <span class="sg__suggestion-email">{{ slot.item.email }}</span>
            </template>
          </AutoComplete>
          <small v-if="erroreEmail" class="sg__error-text">{{ erroreEmail }}</small>
        </div>

        <Button
          type="submit"
          label="Cerca"
          icon="pi pi-search"
          class="sg__submit"
          :disabled="caricamento"
        />
      </form>

      <div class="sg__filters">
        <MultiSelect
          v-model="filtroEventi"
          :options="opzioniEventi"
          optionLabel="label"
          optionValue="value"
          placeholder="Tutti gli eventi"
          display="chip"
          class="sg__filter sg__filter--events"
          @hide="onFiltriCambiati"
        />
        <Calendar
          v-model="filtroDa"
          dateFormat="dd/mm/yy"
          placeholder="Dal"
          showIcon
          showButtonBar
          :maxDate="filtroA || undefined"
          class="sg__filter"
          @date-select="onFiltriCambiati"
          @clear-click="onFiltriCambiati"
        />
        <Calendar
          v-model="filtroA"
          dateFormat="dd/mm/yy"
          placeholder="Al"
          showIcon
          showButtonBar
          :minDate="filtroDa || undefined"
          class="sg__filter"
          @date-select="onFiltriCambiati"
          @clear-click="onFiltriCambiati"
        />
        <Button
          v-if="filtriAttivi"
          label="Azzera filtri"
          icon="pi pi-times"
          class="p-button-text sg__filter-reset"
          @click="azzeraFiltri"
        />
      </div>
    </header>

    <!-- Errore -------------------------------------------------------- -->
    <div v-if="errore" class="sg__panel sg__panel--error" role="alert">
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <div>
        <p class="sg__panel-title">Ricerca non riuscita</p>
        <p class="sg__panel-body">{{ errore }}</p>
      </div>
      <Button label="Riprova" class="p-button-text" @click="cerca(true)" />
    </div>

    <!-- Stato iniziale ------------------------------------------------ -->
    <div v-else-if="!ricercaEffettuata && !caricamento" class="sg__panel sg__panel--empty">
      <i class="pi pi-envelope" aria-hidden="true"></i>
      <div>
        <p class="sg__panel-title">Nessuna ricerca in corso</p>
        <p class="sg__panel-body">
          Digita almeno tre caratteri per vedere gli indirizzi già presenti nei log.
        </p>
      </div>
    </div>

    <!-- Indirizzo senza eventi ---------------------------------------- -->
    <div
      v-else-if="ricercaEffettuata && summary && !summary.trovato"
      class="sg__panel sg__panel--empty"
    >
      <i class="pi pi-inbox" aria-hidden="true"></i>
      <div>
        <p class="sg__panel-title">Nessun evento per {{ emailCercata }}</p>
        <p class="sg__panel-body">
          SendGrid non ha mai registrato eventi per questo indirizzo. Verifica che sia
          scritto correttamente, oppure che la mail sia stata effettivamente inviata.
        </p>
      </div>
    </div>

    <template v-else>
      <!-- Avviso indirizzo bloccato ----------------------------------- -->
      <div v-if="summary && summary.bloccato" class="sg__panel sg__panel--warning" role="status">
        <i class="pi pi-ban" aria-hidden="true"></i>
        <div>
          <p class="sg__panel-title">Indirizzo in soppressione su SendGrid</p>
          <p class="sg__panel-body">
            L'ultimo tentativo di invio è stato scartato il
            {{ dataOra(summary.ultimo_scarto) }}<template v-if="motivoPrincipale">
              con motivo "{{ motivoPrincipale }}"</template>.
            Finché l'indirizzo resta in lista di soppressione, SendGrid non prova
            nemmeno a consegnare i messaggi successivi.
          </p>
        </div>
      </div>

      <div
        v-else-if="summary && summary.spamreport > 0"
        class="sg__panel sg__panel--warning"
        role="status"
      >
        <i class="pi pi-flag" aria-hidden="true"></i>
        <div>
          <p class="sg__panel-title">Segnalazione spam registrata</p>
          <p class="sg__panel-body">
            Il destinatario ha segnalato come spam {{ summary.spamreport }}
            {{ summary.spamreport === 1 ? 'messaggio' : 'messaggi' }}, l'ultima volta il
            {{ dataOra(summary.ultimo_spamreport) }}.
          </p>
        </div>
      </div>

      <!-- Riepilogo indirizzo ----------------------------------------- -->
      <section v-if="summary && summary.trovato" class="sg__summary" aria-label="Riepilogo indirizzo">
        <div class="sg__summary-head">
          <h2 class="sg__summary-email">{{ emailCercata }}</h2>
          <p class="sg__summary-range">
            Dal {{ soloData(summary.primo) }} al {{ soloData(summary.ultimo) }},
            {{ formattaNumero(summary.eventi) }} eventi registrati.
          </p>
        </div>

        <dl class="sg__kpis">
          <div class="sg__kpi">
            <dt class="sg__kpi-label">Richieste di invio</dt>
            <dd class="sg__kpi-value">{{ formattaNumero(summary.presi_in_carico) }}</dd>
            <p class="sg__kpi-note">messe in coda da ASK</p>
          </div>
          <div class="sg__kpi">
            <dt class="sg__kpi-label">Messaggi</dt>
            <dd class="sg__kpi-value">{{ formattaNumero(summary.messaggi) }}</dd>
            <p class="sg__kpi-note">accettati da SendGrid</p>
          </div>
          <div class="sg__kpi">
            <dt class="sg__kpi-label">Consegnati</dt>
            <dd class="sg__kpi-value sg__kpi-value--delivered">
              {{ formattaNumero(summary.messaggi_consegnati) }}
            </dd>
            <p class="sg__kpi-note">{{ percentuale(summary.messaggi_consegnati, summary.messaggi) }}</p>
          </div>
          <div class="sg__kpi">
            <dt class="sg__kpi-label">Aperti</dt>
            <dd class="sg__kpi-value sg__kpi-value--open">
              {{ formattaNumero(summary.messaggi_aperti) }}
            </dd>
            <p class="sg__kpi-note">
              {{ formattaNumero(summary.aperture) }} aperture<template v-if="summary.aperture_automatiche > 0">, di cui {{ formattaNumero(summary.aperture_automatiche) }} automatiche</template>
            </p>
          </div>
          <div class="sg__kpi">
            <dt class="sg__kpi-label">Cliccati</dt>
            <dd class="sg__kpi-value sg__kpi-value--click">
              {{ formattaNumero(summary.messaggi_cliccati) }}
            </dd>
            <p class="sg__kpi-note">
              {{ formattaNumero(summary.click) }} click {{ summary.click === 1 ? 'totale' : 'totali' }}
            </p>
          </div>
          <div class="sg__kpi">
            <dt class="sg__kpi-label">Non consegnati</dt>
            <dd
              class="sg__kpi-value"
              :class="{ 'sg__kpi-value--dropped': nonConsegnati > 0 }"
            >
              {{ formattaNumero(nonConsegnati) }}
            </dd>
            <p class="sg__kpi-note">
              <template v-if="nonConsegnati > 0">{{ percentuale(nonConsegnati, summary.messaggi) }}</template>
              <template v-else-if="summary.deferred > 0">
                {{ formattaNumero(summary.deferred) }} {{ summary.deferred === 1 ? 'rinvio' : 'rinvii' }} in coda
              </template>
              <template v-else>scartati o respinti</template>
            </p>
          </div>
        </dl>
      </section>

      <!-- Tabelle ----------------------------------------------------- -->
      <TabView v-model:activeIndex="tabAttivo" lazy class="sg__tabs" @tab-change="onCambioTab">
        <!-- Messaggi -->
        <TabPanel>
          <template #header>
            <span class="sg__tab-header">
              Messaggi
              <span v-if="summary && summary.trovato" class="sg__tab-count">
                {{ formattaNumero(summary.messaggi) }}
              </span>
            </span>
          </template>

          <p class="sg__tab-lede">
            Un record per messaggio inviato, con il percorso completo dalla presa in
            carico di SendGrid fino ai click. Apri una riga per la cronologia degli eventi.
          </p>

          <SgSkeleton v-if="caricamento" :righe="6" />
          <p v-else-if="!items.length" class="sg__no-rows">
            Nessun messaggio corrisponde ai filtri impostati.
          </p>
          <DataTable
            v-else
            :value="items"
            v-model:expandedRows="righeEspanse"
            dataKey="sg_message_id"
            lazy
            paginator
            :rows="perPage"
            :totalRecords="total"
            :first="(page - 1) * perPage"
            :rowsPerPageOptions="[25, 50, 100]"
            responsiveLayout="scroll"
            class="sg__table"
            @page="onPage"
            @row-expand="onEspandiRiga"
          >
            <Column :expander="true" headerStyle="width: 3rem" />

            <Column header="Stato" headerStyle="width: 12rem">
              <template #body="{ data }">
                <span class="sg__badge" :class="`sg__badge--${data.stato}`">
                  <i :class="icona(data.stato)" aria-hidden="true"></i>
                  {{ etichetta(data.stato) }}
                </span>
              </template>
            </Column>

            <Column header="Percorso" headerStyle="width: 16rem">
              <template #body="{ data }">
                <div
                  class="sg__funnel"
                  :aria-label="descrizionePercorso(data)"
                  :title="descrizionePercorso(data)"
                >
                  <span
                    v-for="tappa in tappe"
                    :key="tappa.evento"
                    class="sg__funnel-step"
                    :class="[
                      `sg__funnel-step--${tappa.evento}`,
                      { 'is-off': !data.conteggi[tappa.evento] },
                    ]"
                  ></span>
                  <span
                    v-if="haProblema(data)"
                    class="sg__funnel-step sg__funnel-step--stop"
                    :class="`sg__funnel-step--${data.stato}`"
                  ></span>
                </div>
              </template>
            </Column>

            <Column header="Primo evento" headerStyle="width: 13rem">
              <template #body="{ data }">
                <span class="sg__num">{{ dataOra(data.primo) }}</span>
              </template>
            </Column>

            <Column header="Ultimo evento" headerStyle="width: 13rem">
              <template #body="{ data }">
                <span class="sg__num">{{ dataOra(data.ultimo) }}</span>
                <span class="sg__hint">{{ tempoRelativo(data.ultimo) }}</span>
              </template>
            </Column>

            <Column header="Aperture" headerStyle="width: 8rem">
              <template #body="{ data }">
                <span class="sg__num">{{ data.conteggi.open || 0 }}</span>
                <span v-if="data.aperture_automatiche > 0" class="sg__hint">
                  {{ data.aperture_automatiche }} automatiche
                </span>
              </template>
            </Column>

            <Column header="Click" headerStyle="width: 8rem">
              <template #body="{ data }">
                <span class="sg__num">{{ data.conteggi.click || 0 }}</span>
                <span v-if="data.url_distinti > 1" class="sg__hint">
                  {{ data.url_distinti }} link
                </span>
              </template>
            </Column>

            <Column header="Origine">
              <template #body="{ data }">
                <span v-if="data.mittente" class="sg__mono">{{ data.mittente }}</span>
                <span v-else class="sg__hint" title="Il Message-ID non riporta un dominio nostro: invio via API di SendGrid, oppure mailer senza dominio configurato">origine non tracciata</span>
              </template>
            </Column>

            <template #expansion="{ data }">
              <div class="sg__expansion">
                <div class="sg__expansion-grid">
                  <div>
                    <h3 class="sg__expansion-title">Cronologia</h3>
                    <SgSkeleton v-if="eventiMessaggio[data.sg_message_id] === undefined" :righe="3" />
                    <Timeline
                      v-else
                      :value="eventiMessaggio[data.sg_message_id]"
                      class="sg__timeline"
                    >
                      <template #marker="{ item }">
                        <span class="sg__dot" :class="`sg__dot--${item.event}`">
                          <i :class="icona(item.event)" aria-hidden="true"></i>
                        </span>
                      </template>
                      <template #content="{ item }">
                        <div class="sg__event">
                          <div class="sg__event-head">
                            <span class="sg__badge" :class="`sg__badge--${item.event}`">
                              {{ etichetta(item.event) }}
                            </span>
                            <span class="sg__num">{{ dataOra(item.timestamp) }}</span>
                          </div>
                          <p v-if="dettaglioEvento(item)" class="sg__event-detail">
                            {{ dettaglioEvento(item) }}
                          </p>
                          <a
                            v-if="item.url"
                            :href="item.url"
                            class="sg__event-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >{{ item.url }}</a>
                          <p v-if="item.useragent" class="sg__event-ua">{{ item.useragent }}</p>
                        </div>
                      </template>
                    </Timeline>
                  </div>

                  <div>
                    <h3 class="sg__expansion-title">Dettagli tecnici</h3>
                    <dl class="sg__facts">
                      <dt>Message ID</dt>
                      <dd class="sg__mono sg__mono--wrap">{{ data.sg_message_id }}</dd>
                      <dt>Eventi</dt>
                      <dd>{{ data.eventi }}</dd>
                      <template v-if="data.mittente">
                        <dt>Dominio mittente</dt>
                        <dd class="sg__mono">{{ data.mittente }}</dd>
                      </template>
                      <template v-if="data.risposta">
                        <dt>Risposta SMTP</dt>
                        <dd class="sg__mono sg__mono--wrap">{{ data.risposta }}</dd>
                      </template>
                      <template v-if="data.motivo">
                        <dt>Motivo dello scarto</dt>
                        <dd>{{ data.motivo }}</dd>
                      </template>
                      <template v-if="data.bounce_classification">
                        <dt>Classificazione rimbalzo</dt>
                        <dd>{{ data.bounce_classification }}</dd>
                      </template>
                      <template v-if="data.motivo_rinvio">
                        <dt>Motivo del rinvio</dt>
                        <dd>
                          {{ data.motivo_rinvio }}
                          <template v-if="data.tentativi !== null">
                            (tentativo {{ data.tentativi }})
                          </template>
                        </dd>
                      </template>
                      <template v-if="data.ip">
                        <dt>Ultimo IP</dt>
                        <dd class="sg__mono">{{ data.ip }}</dd>
                      </template>
                    </dl>
                  </div>
                </div>
              </div>
            </template>
          </DataTable>
        </TabPanel>

        <!-- Invii ASK -->
        <TabPanel>
          <template #header>
            <span class="sg__tab-header">
              Invii ASK
              <span v-if="summary && summary.trovato" class="sg__tab-count">
                {{ formattaNumero(summary.presi_in_carico) }}
              </span>
            </span>
          </template>

          <p class="sg__tab-lede">
            Le mail che ASK ha messo in coda verso SendGrid. Se una comunicazione manca
            qui, non è mai partita dai nostri sistemi.
          </p>

          <SgSkeleton v-if="caricamento" :righe="6" />
          <p v-else-if="!items.length" class="sg__no-rows">
            Nessun invio registrato da ASK per questo indirizzo.
          </p>
          <DataTable
            v-else
            :value="items"
            dataKey="id"
            lazy
            paginator
            :rows="perPage"
            :totalRecords="total"
            :first="(page - 1) * perPage"
            :rowsPerPageOptions="[25, 50, 100]"
            responsiveLayout="scroll"
            class="sg__table"
            @page="onPage"
          >
            <Column header="Oggetto">
              <template #body="{ data }">{{ data.titolo || 'Senza oggetto' }}</template>
            </Column>
            <Column header="Presa in carico" headerStyle="width: 20rem">
              <template #body="{ data }">
                <span class="sg__num">{{ dataOra(data.timestamp) }}</span>
                <span class="sg__hint">{{ tempoRelativo(data.timestamp) }}</span>
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <!-- Eventi grezzi -->
        <TabPanel>
          <template #header>
            <span class="sg__tab-header">
              Eventi
              <span v-if="summary && summary.trovato" class="sg__tab-count">
                {{ formattaNumero(summary.eventi) }}
              </span>
            </span>
          </template>

          <p class="sg__tab-lede">
            Ogni callback ricevuto da SendGrid, in ordine cronologico inverso.
          </p>

          <SgSkeleton v-if="caricamento" :righe="6" />
          <p v-else-if="!items.length" class="sg__no-rows">
            Nessun evento corrisponde ai filtri impostati.
          </p>
          <DataTable
            v-else
            :value="items"
            dataKey="id"
            lazy
            paginator
            :rows="perPage"
            :totalRecords="total"
            :first="(page - 1) * perPage"
            :rowsPerPageOptions="[25, 50, 100]"
            responsiveLayout="scroll"
            class="sg__table"
            @page="onPage"
          >
            <Column header="Evento" headerStyle="width: 12rem">
              <template #body="{ data }">
                <span class="sg__badge" :class="`sg__badge--${data.event}`">
                  <i :class="icona(data.event)" aria-hidden="true"></i>
                  {{ etichetta(data.event) }}
                </span>
              </template>
            </Column>
            <Column header="Data e ora" headerStyle="width: 14rem">
              <template #body="{ data }">
                <span class="sg__num">{{ dataOra(data.timestamp) }}</span>
                <span class="sg__hint">{{ tempoRelativo(data.timestamp) }}</span>
              </template>
            </Column>
            <Column header="Dettaglio">
              <template #body="{ data }">
                <p v-if="dettaglioEvento(data)" class="sg__cell-detail">
                  {{ dettaglioEvento(data) }}
                </p>
                <a
                  v-if="data.url"
                  :href="data.url"
                  class="sg__event-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ data.url }}</a>
                <p v-if="data.useragent" class="sg__event-ua">{{ data.useragent }}</p>
                <span v-if="!dettaglioEvento(data) && !data.url && !data.useragent" class="sg__hint">
                  nessun dettaglio
                </span>
              </template>
            </Column>
            <Column header="Messaggio" headerStyle="width: 15rem">
              <template #body="{ data }">
                <span v-if="data.message_key" class="sg__mono">{{ data.message_key }}</span>
                <span v-else class="sg__hint">non correlato</span>
              </template>
            </Column>
            <Column header="IP" headerStyle="width: 10rem">
              <template #body="{ data }">
                <span v-if="data.ip" class="sg__mono">{{ data.ip }}</span>
                <span v-else class="sg__hint">-</span>
              </template>
            </Column>
          </DataTable>
        </TabPanel>
      </TabView>
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, h, ref } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import MultiSelect from 'primevue/multiselect';
import TabPanel from 'primevue/tabpanel';
import TabView from 'primevue/tabview';
import Timeline from 'primevue/timeline';
import Service from '@/services/ask-caricamento-file/caricamento-file';

// Righe grigie di attesa: stessa forma delle righe reali, niente spinner.
const SgSkeleton = (props) =>
  h(
    'div',
    { class: 'sg__skeleton' },
    Array.from({ length: props.righe || 4 }, () => h('span', { class: 'sg__skeleton-row' }))
  );
SgSkeleton.props = { righe: { type: Number, default: 4 } };

const VISTE = ['messaggi', 'ask', 'eventi'];

const ETICHETTE = {
  SENDINGASK: 'Presa in carico',
  processed: 'Accettata',
  delivered: 'Consegnata',
  deferred: 'Rinviata',
  dropped: 'Scartata',
  bounce: 'Respinta',
  open: 'Aperta',
  click: 'Click',
  spamreport: 'Segnalata come spam',
  unsubscribe: 'Disiscritto',
  group_unsubscribe: 'Disiscritto dal gruppo',
  group_resubscribe: 'Reiscritto al gruppo',
};

const ICONE = {
  SENDINGASK: 'pi pi-inbox',
  processed: 'pi pi-send',
  delivered: 'pi pi-check',
  deferred: 'pi pi-clock',
  dropped: 'pi pi-ban',
  bounce: 'pi pi-replay',
  open: 'pi pi-envelope',
  click: 'pi pi-external-link',
  spamreport: 'pi pi-flag',
  unsubscribe: 'pi pi-user-minus',
  group_unsubscribe: 'pi pi-user-minus',
  group_resubscribe: 'pi pi-user-plus',
};

const tappe = [
  { evento: 'processed' },
  { evento: 'delivered' },
  { evento: 'open' },
  { evento: 'click' },
];

const opzioniEventi = Object.keys(ETICHETTE).map((value) => ({
  value,
  label: ETICHETTE[value],
}));

const instance = getCurrentInstance();
const service = new Service(instance.appContext.config.globalProperties.$eventBus);

const email = ref('');
const emailCercata = ref('');
const suggerimenti = ref([]);
const erroreEmail = ref('');
const errore = ref('');

const filtroEventi = ref([]);
const filtroDa = ref(null);
const filtroA = ref(null);

const caricamento = ref(false);
const ricercaEffettuata = ref(false);
const summary = ref(null);
const items = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(25);
const tabAttivo = ref(0);
const righeEspanse = ref([]);
const eventiMessaggio = ref({});

const vista = computed(() => VISTE[tabAttivo.value] || 'messaggi');

const filtriAttivi = computed(
  () => filtroEventi.value.length > 0 || filtroDa.value !== null || filtroA.value !== null
);

// Come gli altri contatori della striscia, conta messaggi e non eventi: un
// indirizzo in soppressione accumula piu' scarti sullo stesso messaggio.
const nonConsegnati = computed(() => summary.value?.messaggi_non_consegnati ?? 0);

const motivoPrincipale = computed(() => {
  const motivi = summary.value?.motivi ?? [];
  return motivi.length ? motivi[0].motivo : null;
});

// ---------------------------------------------------------------- ricerca

const testoEmail = (valore) =>
  (typeof valore === 'string' ? valore : valore?.email ?? '').trim();

async function cercaSuggerimenti(evento) {
  const q = (evento.query || '').trim();
  if (q.length < 3) {
    suggerimenti.value = [];
    return;
  }
  try {
    suggerimenti.value = await service.suggestSendgridEmail(q);
  } catch (e) {
    suggerimenti.value = [];
  }
}

function onSuggerimentoScelto(evento) {
  email.value = evento.value.email;
  cerca(true);
}

function onFiltriCambiati() {
  if (ricercaEffettuata.value) cerca(true);
}

function azzeraFiltri() {
  filtroEventi.value = [];
  filtroDa.value = null;
  filtroA.value = null;
  if (ricercaEffettuata.value) cerca(true);
}

function onCambioTab() {
  righeEspanse.value = [];
  if (ricercaEffettuata.value) carica(1, false);
}

function onPage(evento) {
  perPage.value = evento.rows;
  carica(Math.floor(evento.first / evento.rows) + 1, false);
}

async function onEspandiRiga(evento) {
  const id = evento.data.sg_message_id;
  if (eventiMessaggio.value[id] !== undefined) return;
  try {
    const eventi = await service.getSendgridMessageEvents(emailCercata.value, id);
    eventiMessaggio.value = { ...eventiMessaggio.value, [id]: eventi };
  } catch (e) {
    eventiMessaggio.value = { ...eventiMessaggio.value, [id]: [] };
  }
}

function cerca(riparti = true) {
  const valore = testoEmail(email.value);
  erroreEmail.value = '';

  if (!valore) {
    erroreEmail.value = 'Inserisci un indirizzo email.';
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valore)) {
    erroreEmail.value = 'Formato email non valido.';
    return;
  }

  emailCercata.value = valore;
  if (riparti) {
    righeEspanse.value = [];
    eventiMessaggio.value = {};
  }
  carica(1, true);
}

async function carica(paginaRichiesta, conRiepilogo) {
  caricamento.value = true;
  errore.value = '';
  page.value = paginaRichiesta;
  items.value = [];

  try {
    const risposta = await service.findLogByEmail({
      email: emailCercata.value,
      view: vista.value,
      page: paginaRichiesta,
      perPage: perPage.value,
      events: filtroEventi.value,
      from: isoData(filtroDa.value),
      to: isoData(filtroA.value),
      withSummary: conRiepilogo || summary.value === null,
    });

    if (risposta?.summary) summary.value = risposta.summary;
    items.value = risposta?.items ?? [];
    total.value = risposta?.total ?? 0;
    ricercaEffettuata.value = true;
  } catch (e) {
    errore.value =
      e?.response?.data?.message ||
      e?.message ||
      'Il servizio non ha risposto. Riprova tra qualche istante.';
    items.value = [];
    total.value = 0;
    ricercaEffettuata.value = true;
  } finally {
    caricamento.value = false;
  }
}

// ---------------------------------------------------------------- helper

const etichetta = (evento) => ETICHETTE[evento] || evento;
const icona = (evento) => ICONE[evento] || 'pi pi-circle-fill';

const haProblema = (riga) =>
  riga.conteggi.dropped > 0 || riga.conteggi.bounce > 0 || riga.conteggi.spamreport > 0;

function descrizionePercorso(riga) {
  const raggiunte = tappe
    .filter((t) => riga.conteggi[t.evento] > 0)
    .map((t) => ETICHETTE[t.evento]);
  if (haProblema(riga)) raggiunte.push(ETICHETTE[riga.stato]);
  return raggiunte.length ? raggiunte.join(', ') : 'Nessuna tappa raggiunta';
}

function dettaglioEvento(evento) {
  if (evento.reason) return evento.reason;
  if (evento.response) return evento.response;
  if (evento.status) return `Stato SMTP ${evento.status}`;
  if (evento.event === 'open' && evento.sg_machine_open === 1) {
    return 'Apertura automatica del client di posta, non necessariamente letta.';
  }
  if (evento.title) return evento.title;
  return '';
}

const formattaNumero = (n) => new Intl.NumberFormat('it-IT').format(n ?? 0);

function percentuale(parte, totale) {
  if (!totale) return 'nessun messaggio';
  return `${Math.round((parte / totale) * 100)}% dei messaggi`;
}

const FMT_DATA_ORA = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const FMT_DATA = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dataOra = (ts) => (ts ? FMT_DATA_ORA.format(new Date(ts * 1000)) : '-');
const soloData = (ts) => (ts ? FMT_DATA.format(new Date(ts * 1000)) : '-');

function tempoRelativo(ts) {
  if (!ts) return '';
  const secondi = Math.round(Date.now() / 1000 - ts);
  if (secondi < 60) return 'pochi secondi fa';
  const minuti = Math.round(secondi / 60);
  if (minuti < 60) return `${minuti} min fa`;
  const ore = Math.round(minuti / 60);
  if (ore < 24) return `${ore} ${ore === 1 ? 'ora' : 'ore'} fa`;
  const giorni = Math.round(ore / 24);
  if (giorni < 31) return `${giorni} ${giorni === 1 ? 'giorno' : 'giorni'} fa`;
  const mesi = Math.round(giorni / 30);
  if (mesi < 12) return `${mesi} ${mesi === 1 ? 'mese' : 'mesi'} fa`;
  const anni = Math.round(mesi / 12);
  return `${anni} ${anni === 1 ? 'anno' : 'anni'} fa`;
}

function isoData(data) {
  if (!data) return null;
  const d = new Date(data);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const gg = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${gg}`;
}
</script>

<style scoped lang="scss">
.sg {
  /* Palette semantica degli eventi: testo scuro su tinta chiara, stesso peso
     per tutte le famiglie, così nessuno stato urla più degli altri. */
  --sg-ink: #17212b;
  --sg-ink-soft: #5b6875;
  --sg-ink-faint: #8a95a1;
  --sg-line: #e3e7ec;
  --sg-surface: #ffffff;
  --sg-surface-alt: #f6f8fa;
  --sg-header-bg: #e8edf3;
  --sg-header-ink: #2f3a47;
  --sg-header-line: #ccd5e0;
  --sg-radius: 10px;

  --sg-SENDINGASK: #475569;
  --sg-SENDINGASK-bg: #eef2f7;
  --sg-processed: #1d4ed8;
  --sg-processed-bg: #e9effd;
  --sg-delivered: #15803d;
  --sg-delivered-bg: #e6f4ea;
  --sg-deferred: #b45309;
  --sg-deferred-bg: #fdf0dd;
  --sg-dropped: #b91c1c;
  --sg-dropped-bg: #fce9e9;
  --sg-bounce: #9f1239;
  --sg-bounce-bg: #fce9ee;
  --sg-open: #0f766e;
  --sg-open-bg: #e2f3f1;
  --sg-click: #6d28d9;
  --sg-click-bg: #efe8fd;
  --sg-spamreport: #a21caf;
  --sg-spamreport-bg: #fae9fb;
  --sg-unsubscribe: #52525b;
  --sg-unsubscribe-bg: #f2f2f4;

  color: var(--sg-ink);
  background: var(--sg-surface);
  padding: 1.5rem 1.5rem 3rem;
  max-width: 1500px;
  margin: 0 auto;
}

/* ---------------------------------------------------------- intestazione */

.sg__header {
  border-bottom: 1px solid var(--sg-line);
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
}

.sg__intro {
  max-width: 62ch;
}

.sg__title {
  font-size: 1.6rem;
  font-weight: 650;
  letter-spacing: -0.015em;
  margin: 0 0 0.35rem;
  color: var(--sg-ink);
}

.sg__lede {
  margin: 0;
  color: var(--sg-ink-soft);
  line-height: 1.55;
}

.sg__form {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.sg__field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1 1 auto;
  max-width: 34rem;
}

.sg__label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: var(--sg-ink-soft);
}

.sg__autocomplete {
  width: 100%;
}

.sg__autocomplete :deep(.sg__input) {
  width: 100%;
  border-radius: var(--sg-radius);
}

.sg__submit {
  border-radius: var(--sg-radius);
  min-width: 8.5rem;
  white-space: nowrap;
}

.sg__error-text {
  color: var(--sg-dropped);
  font-size: 0.8rem;
}

.sg__suggestion-email {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.85rem;
}

.sg__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.9rem;
}

.sg__filter :deep(.p-inputtext),
.sg__filter.p-multiselect,
.sg__filter :deep(.p-multiselect) {
  border-radius: var(--sg-radius);
}

.sg__filter--events {
  min-width: 15rem;
  max-width: 32rem;
}

.sg__filter-reset {
  border-radius: var(--sg-radius);
}

/* ------------------------------------------------------------- pannelli */

.sg__panel {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1.1rem 1.25rem;
  border: 1px solid var(--sg-line);
  border-radius: var(--sg-radius);
  background: var(--sg-surface-alt);
  margin-bottom: 1.5rem;
}

.sg__panel > i {
  font-size: 1.15rem;
  line-height: 1.4;
  color: var(--sg-ink-soft);
}

.sg__panel-title {
  margin: 0 0 0.2rem;
  font-weight: 620;
}

.sg__panel-body {
  margin: 0;
  color: var(--sg-ink-soft);
  line-height: 1.55;
  max-width: 78ch;
}

.sg__panel--error {
  border-color: #f1c9c9;
  background: var(--sg-dropped-bg);
}
.sg__panel--error > i,
.sg__panel--error .sg__panel-title {
  color: var(--sg-dropped);
}

.sg__panel--warning {
  border-color: #f0d5a8;
  background: var(--sg-deferred-bg);
}
.sg__panel--warning > i,
.sg__panel--warning .sg__panel-title {
  color: var(--sg-deferred);
}

.sg__panel--empty {
  align-items: center;
}

/* ------------------------------------------------------------ riepilogo */

.sg__summary {
  margin-bottom: 1.75rem;
}

.sg__summary-head {
  margin-bottom: 1rem;
}

.sg__summary-email {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 620;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  word-break: break-all;
}

.sg__summary-range {
  margin: 0.25rem 0 0;
  color: var(--sg-ink-soft);
  font-size: 0.86rem;
}

.sg__kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0;
  margin: 0;
  border-top: 1px solid var(--sg-line);
  border-bottom: 1px solid var(--sg-line);
}

.sg__kpi {
  padding: 0.95rem 1.1rem;
  border-left: 1px solid var(--sg-line);
}

.sg__kpi:first-child {
  border-left: 0;
  padding-left: 0;
}

.sg__kpi-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--sg-ink-soft);
  margin-bottom: 0.3rem;
}

.sg__kpi-value {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
}

.sg__kpi-value--delivered { color: var(--sg-delivered); }
.sg__kpi-value--open { color: var(--sg-open); }
.sg__kpi-value--click { color: var(--sg-click); }
.sg__kpi-value--dropped { color: var(--sg-dropped); }

.sg__kpi-note {
  margin: 0.25rem 0 0;
  font-size: 0.74rem;
  color: var(--sg-ink-faint);
  line-height: 1.35;
}

/* ---------------------------------------------------------------- tabelle */

.sg__tabs :deep(.p-tabview-nav) {
  border-bottom: 1px solid var(--sg-line);
}

.sg__tabs :deep(.p-tabview-panels) {
  padding: 1.25rem 0 0;
  background: transparent;
}

.sg__tab-header {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.sg__tab-count {
  font-size: 0.72rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: var(--sg-surface-alt);
  color: var(--sg-ink-soft);
}

.sg__tab-lede {
  margin: 0 0 1rem;
  color: var(--sg-ink-soft);
  font-size: 0.86rem;
  max-width: 80ch;
}

.sg__no-rows {
  padding: 2rem 0;
  color: var(--sg-ink-soft);
  text-align: center;
}

.sg__table :deep(.p-datatable-thead > tr > th) {
  font-size: 0.76rem;
  font-weight: 650;
  letter-spacing: 0.02em;
  background: var(--sg-header-bg);
  border-bottom: 1px solid var(--sg-header-line);
  padding: 0.7rem 1rem;
}

/* CaricamentoAnnullamentoPratica.vue ha un blocco <style> non scoped che forza
   `color: #fff !important` su `.p-datatable .p-column-header-content`: essendo
   globale colpisce gli header di ogni DataTable dell'app, questa compresa.
   Contro un !important la sola specificita' non basta, serve !important anche
   qui. Il fallback letterale copre il caso in cui il token non risolva. */
.sg__table :deep(.p-datatable-thead > tr > th),
.sg__table :deep(.p-datatable-thead > tr > th .p-column-header-content),
.sg__table :deep(.p-datatable-thead > tr > th .p-column-title) {
  color: var(--sg-header-ink, #2f3a47) !important;
}

.sg__table :deep(.p-datatable-tbody > tr) {
  background: var(--sg-surface);
  color: var(--sg-ink);
}

.sg__table :deep(.p-datatable-tbody > tr > td) {
  font-size: 0.86rem;
  vertical-align: top;
  border-bottom: 1px solid var(--sg-line);
}

.sg__table :deep(.p-paginator) {
  background: transparent;
  color: var(--sg-ink-soft);
}

.sg__num {
  display: block;
  font-variant-numeric: tabular-nums;
}

.sg__hint {
  display: block;
  font-size: 0.73rem;
  color: var(--sg-ink-faint);
  margin-top: 0.1rem;
}

.sg__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  color: var(--sg-ink-soft);
}

.sg__mono--wrap {
  word-break: break-all;
}

/* ---------------------------------------------------------------- badge */

.sg__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 600;
  white-space: nowrap;
  background: var(--sg-SENDINGASK-bg);
  color: var(--sg-SENDINGASK);
}

.sg__badge i {
  font-size: 0.72rem;
}

@each $evento in SENDINGASK, processed, delivered, deferred, dropped, bounce, open, click,
  spamreport, unsubscribe
{
  .sg__badge--#{$evento} {
    background: var(--sg-#{$evento}-bg);
    color: var(--sg-#{$evento});
  }
  .sg__dot--#{$evento} {
    background: var(--sg-#{$evento}-bg);
    color: var(--sg-#{$evento});
  }
  .sg__funnel-step--#{$evento} {
    background: var(--sg-#{$evento});
  }
}

.sg__badge--group_unsubscribe,
.sg__badge--group_resubscribe {
  background: var(--sg-unsubscribe-bg);
  color: var(--sg-unsubscribe);
}

/* --------------------------------------------------------------- percorso */

.sg__funnel {
  display: flex;
  gap: 3px;
  align-items: center;
  cursor: default;
}

.sg__funnel-step {
  height: 8px;
  width: 30px;
  border-radius: 4px;
}

.sg__funnel-step.is-off {
  background: #e6e9ed;
}

.sg__funnel-step--stop {
  /* Il colore arriva dalla classe di stato affiancata (dropped, bounce, spam). */
  width: 14px;
}

/* -------------------------------------------------------------- espansione */

.sg__expansion {
  padding: 1.25rem 1rem 1.5rem 3rem;
  background: var(--sg-surface-alt);
}

.sg__expansion-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 2.5rem;
}

.sg__expansion-title {
  font-size: 0.82rem;
  font-weight: 650;
  color: var(--sg-ink-soft);
  margin: 0 0 0.9rem;
}

.sg__timeline :deep(.p-timeline-event-opposite) {
  display: none;
}

.sg__timeline :deep(.p-timeline-event-content) {
  padding-bottom: 1.1rem;
}

.sg__dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  font-size: 0.7rem;
  background: var(--sg-SENDINGASK-bg);
  color: var(--sg-SENDINGASK);
}

.sg__event-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.sg__event-head .sg__num {
  display: inline;
  font-size: 0.8rem;
  color: var(--sg-ink-soft);
}

.sg__event-detail,
.sg__cell-detail {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: var(--sg-ink-soft);
  line-height: 1.45;
  max-width: 70ch;
}

.sg__event-link {
  display: inline-block;
  margin-top: 0.3rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  color: var(--sg-click);
  word-break: break-all;
  max-width: 60ch;
}

.sg__event-ua {
  margin: 0.25rem 0 0;
  font-size: 0.72rem;
  color: var(--sg-ink-faint);
  max-width: 60ch;
}

.sg__facts {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 0.45rem 1rem;
  margin: 0;
  font-size: 0.8rem;
}

.sg__facts dt {
  color: var(--sg-ink-faint);
  white-space: nowrap;
}

.sg__facts dd {
  margin: 0;
  min-width: 0;
}

/* ------------------------------------------------------------- skeleton */

.sg__skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.sg__skeleton :deep(.sg__skeleton-row) {
  display: block;
  height: 2.4rem;
  border-radius: var(--sg-radius);
  background: linear-gradient(90deg, #eef1f4 25%, #e4e8ed 37%, #eef1f4 63%);
  background-size: 400% 100%;
}

@media (prefers-reduced-motion: no-preference) {
  .sg__skeleton :deep(.sg__skeleton-row) {
    animation: sg-shimmer 1.4s ease infinite;
  }
}

@keyframes sg-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* ------------------------------------------------------------ responsive */

@media (max-width: 1200px) {
  .sg__kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .sg__kpi:nth-child(3n + 1) {
    border-left: 0;
    padding-left: 0;
  }
  .sg__kpi:nth-child(n + 4) {
    border-top: 1px solid var(--sg-line);
  }
  .sg__expansion-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.75rem;
  }
}

@media (max-width: 768px) {
  .sg {
    padding: 1rem 1rem 2.5rem;
  }
  .sg__form {
    flex-direction: column;
    align-items: stretch;
  }
  .sg__field {
    max-width: none;
  }
  .sg__submit {
    width: 100%;
  }
  .sg__filter--events {
    min-width: 0;
    width: 100%;
  }
  .sg__kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sg__kpi {
    border-left: 0;
    padding-left: 0;
  }
  .sg__kpi:nth-child(2n) {
    border-left: 1px solid var(--sg-line);
    padding-left: 1.1rem;
  }
  .sg__kpi:nth-child(n + 3) {
    border-top: 1px solid var(--sg-line);
  }
  .sg__expansion {
    padding-left: 1rem;
  }
}
</style>
